// PostFilterButtons.js
import React, { useEffect, useRef, useState } from "react";
import { Button, DatePicker, message } from "antd";
import moment from "moment";
import {
  IoEarthOutline,
  IoPeopleOutline,
  IoSchoolOutline,
  IoPlay,
  IoPause,
  IoCalendarOutline,
  IoCloseCircle,
} from "react-icons/io5";

const UMT_RADIO_STREAM_URL = "https://studio25.radiolize.com/radio/8050/radio.mp3";

const PostFilterButtons = ({
  selectedPostFor,
  handlePostForChange,
  isLoggedIn,
  userType,
  dateRange,
  handleDateChange,
}) => {
  const today = moment();
  const ticketStatusId = 4; // Default ticket status ID
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const selectedDate = dateRange[0];

  const handleStartDateChange = (startDate) => {
    // Pass both start date and today as the fixed end date to the handleDateChange function
    handleDateChange([startDate, today]);
  };

  const clearDateFilter = (event) => {
    event.stopPropagation();
    handleDateChange([null, today]);
  };

  // Disable future dates in the date picker
  const disableFutureDates = (current) => {
    return current && current > today.endOf("day");
  };

  return (
    <>
      <div className="filter-buttons">
        {/* Buttons */}
        <Button
          type={selectedPostFor === 1 ? "primary" : "default"}
          onClick={() => handlePostForChange(1, ticketStatusId)}
          icon={<IoEarthOutline style={{ verticalAlign: "-2px" }} />}
          className="button-custom"
        >
          Public
        </Button>

        {isLoggedIn && (
          <>
            {userType === "student" ? (
              <Button
                type={selectedPostFor === 2 ? "primary" : "default"}
                onClick={() => handlePostForChange(2, ticketStatusId)}
                icon={<IoPeopleOutline style={{ verticalAlign: "-2px" }} />}
                className="button-custom"
              >
                Participants
              </Button>
            ) : (
              <>
                <Button
                  type={selectedPostFor === 2 ? "primary" : "default"}
                  onClick={() => handlePostForChange(2, ticketStatusId)}
                  icon={<IoPeopleOutline style={{ verticalAlign: "-2px" }} />}
                  className="button-custom"
                >
                  Participants
                </Button>
                <Button
                  type={selectedPostFor === 3 ? "primary" : "default"}
                  onClick={() => handlePostForChange(3, ticketStatusId)}
                  icon={<IoSchoolOutline style={{ verticalAlign: "-2px" }} />}
                  className="button-custom"
                >
                  Faculty/Staff
                </Button>
              </>
            )}
          </>
        )}

        {/* Date filter, styled and triggered like the other pill buttons */}
        <div className="date-filter-trigger">
          <Button
            type={selectedDate ? "primary" : "default"}
            icon={<IoCalendarOutline style={{ verticalAlign: "-2px" }} />}
            onClick={() => setIsDatePickerOpen(true)}
            className="button-custom"
          >
            {selectedDate ? moment(selectedDate).format("MMM D, YYYY") : "Filter by date"}
          </Button>
          {selectedDate && (
            <span
              className="date-filter-clear"
              onClick={clearDateFilter}
              role="button"
              aria-label="Clear date filter"
            >
              <IoCloseCircle />
            </span>
          )}
          <DatePicker
            format="YYYY-MM-DD"
            value={selectedDate}
            open={isDatePickerOpen}
            onOpenChange={setIsDatePickerOpen}
            onChange={handleStartDateChange}
            disabledDate={disableFutureDates}
            className="date-filter-hidden-input"
          />
        </div>
      </div>
    </>
  );
};

// A small, always-visible chip (not tucked behind "Read more") that plays
// the live stream right here — an actual play/pause control, not a link
// that redirects away to play it.
export const UmtRadioCard = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Stop the stream if the chip ever leaves the page while playing.
    return () => audioRef.current?.pause();
  }, []);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    audio
      .play()
      .then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        message.error("Couldn't start UMT Radio. Please try again.");
      });
  };

  return (
    <button
      type="button"
      onClick={togglePlayback}
      className={`umt-radio-chip${isPlaying ? " is-playing" : ""}`}
      aria-pressed={isPlaying}
      aria-label={isPlaying ? "Pause UMT Radio" : "Play UMT Radio live stream"}
    >
      <audio
        ref={audioRef}
        src={UMT_RADIO_STREAM_URL}
        preload="none"
        onError={() => {
          setIsPlaying(false);
          setIsLoading(false);
          message.error("UMT Radio stream is unavailable right now.");
        }}
      />
      <span className="umt-radio-play-icon">
        {isLoading ? (
          <span className="umt-radio-spinner" />
        ) : isPlaying ? (
          <IoPause />
        ) : (
          <IoPlay />
        )}
      </span>
      {/* Little bouncing equalizer bars — only animate while actually playing */}
      <span className="umt-radio-bars" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span>
        {isPlaying ? "Now Playing: UMT Radio" : "Listen Live: UMT Radio"}{" "}
        <em>— Suno UMT Ki Awaz!</em>
      </span>
    </button>
  );
};

export default PostFilterButtons;
